<script setup lang="ts">
import { apiGetTheaterInfo } from '@/apis/theater'
import type { IResTheaterInfo } from '@/types/apis/theater'
import { X } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import type { IRefVideoItem } from '../../types'
import { useAppStore } from '@/stores/app'
const appStore = useAppStore()

const props = defineProps<{
  scriptId: number
  activeEpisodeId?: number
  episodes?: IRefVideoItem[]
  hideHeader?: boolean
}>()

const emit = defineEmits<{
  (e: 'select-episode', episodeId: number): void
  (e: 'close'): void
}>()

const theaterInfo = ref<IResTheaterInfo>()

onMounted(async () => {
  if (!props.scriptId || theaterInfo.value) return
  const response = await apiGetTheaterInfo({ scriptId: props.scriptId })
  theaterInfo.value = response.Body
})

// 宫格数据：优先使用 theaterInfo.Episodes（有 SequenceNumber、IsFree、VideoUrl）
const gridEpisodes = computed(() => {
  if (theaterInfo.value?.Episodes?.length) {
    return theaterInfo.value.Episodes.map(ep => ({
      id: ep.Id,
      seq: ep.SequenceNumber,
      isActive: ep.Id === props.activeEpisodeId,
      hasVideo: !!ep.VideoUrl,
      isFree: ep.IsFree,
    }))
  }
  return []
})

const fn = (episodeId: number) => {
  emit('select-episode', episodeId)
}
</script>

<template>
  <div
    class="relative z-50 flex h-full w-[25vw] shrink-0 overflow-hidden bg-black/40 text-white"
    @wheel.stop
    @touchmove.stop
  >
    <div class="flex h-full w-full flex-col">
      <!-- 顶部标题栏 -->
      <div v-if="!hideHeader" class="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3">
        <div class="flex items-center gap-1.5 min-w-0">
          <span class="truncate text-lg font-semibold text-gray-100">{{ theaterInfo?.Name }}</span>
          <!-- <span class="shrink-0 text-sm font-normal text-gray-400">（{{ gridEpisodes.length }}集全）</span> -->
        </div>
        <button
          @click="appStore.hideEpisodes(scriptId)"
          class="cursor-pointer! ml-2 shrink-0 text-white/60 transition-colors hover:text-white"
        >
          <X :size="20" />
        </button>
      </div>

      <!-- 剧集元信息：封面 + 标签 + 简介 -->
      <div class="shrink-0 space-y-4 border-b border-white/10 py-4 mx-4">
        <div class="flex gap-3">
          <!-- 封面图 -->
          <img
            v-if="theaterInfo?.CoverImageUrl"
            :src="theaterInfo.CoverImageUrl"
            class="size-16 shrink-0 rounded-lg object-cover"
          />
          <!-- 简介 -->
          <p class="line-clamp-4 text-sm leading-relaxed text-white/90 text-justify">
            {{ theaterInfo?.Description || '暂无简介' }}
          </p>
        </div>
        <!-- 标签 -->
        <div v-if="theaterInfo?.CategoryTags?.length" class="flex flex-wrap gap-1.5">
          <span
            v-for="tag in theaterInfo.CategoryTags"
            :key="tag.CategoryId"
            class="rounded-md bg-white/10 px-4 py-1 text-sm text-gray-300"
          >
            {{ tag.Name }}
          </span>
        </div>
      </div>

      <!-- 剧集列表（宫格） -->
      <div class="flex flex-1 flex-col overflow-hidden p-4">
        <h3 class="mb-3 shrink-0 text-base text-white/90">剧集列表</h3>
        <div class="overflow-y-auto">
          <div class="grid grid-cols-5 gap-2">
            <button
              v-for="(ep, index) in gridEpisodes"
              :key="ep.id"
              class="relative flex cursor-pointer items-center justify-center rounded-lg disabled:cursor-not-allowed border py-3 text-sm transition-colors"
              :class="[
                ep.isActive
                  ? 'border-purple-500/70 bg-purple-500/20 font-bold text-purple-300'
                  : ep.hasVideo
                    ? 'border-white/10 bg-white/5 text-gray-200 hover:bg-white/10'
                    : 'cursor-not-allowed border-yellow-400/10 bg-yellow-400/5 text-gray-500 opacity-60',
              ]"
              @click="fn(ep.id)"
            >
              {{ index + 1 }}
              <span v-if="!ep.isFree" class="absolute right-1 top-0.5 text-[9px] font-bold italic text-yellow-400"
                >VIP</span
              >
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
